import type { ApiResponse } from "../../../shared/types/common";
import type {
  QuoteInquiryDetail,
  QuoteInquiryPageResponse,
  QuoteInquiryStatus,
  ReplyQuoteInquiryRequest,
  UpdateQuoteInquiryStatusRequest,
} from "../../../domain/types/quote-inquiry";
import { httpClient } from "../http-client";

export class CompanyQuoteInquiryApiService {
  /**
   * 견적 문의 목록 조회
   */
  async getQuoteInquiries(
    companyId: number,
    params: {
      status?: QuoteInquiryStatus;
      keyword?: string;
      page?: number;
      size?: number;
    }
  ): Promise<QuoteInquiryPageResponse> {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append("status", params.status);
    if (params.keyword) queryParams.append("keyword", params.keyword);
    if (params.page !== undefined)
      queryParams.append("page", params.page.toString());
    if (params.size !== undefined)
      queryParams.append("size", params.size.toString());

    const url = `/api/company/${companyId}/quote-inquiries?${queryParams.toString()}`;

    // 백엔드 API 응답 형식: items 배열 사용
    const response = await httpClient.get<{
      data: {
        items: QuoteInquiryPageResponse["content"];
        page: number;
        size: number;
        totalElements: number;
        totalPages: number;
      };
      errorMsg: string | null;
      errorCode: string | null;
    }>(url);

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "견적 문의 목록을 가져오지 못했습니다."
      );
    }

    // items를 content로 매핑
    const apiData = response.data.data;
    return {
      content: apiData.items || [],
      page: apiData.page,
      size: apiData.size,
      totalElements: apiData.totalElements,
      totalPages: apiData.totalPages,
    };
  }

  /**
   * 견적 문의 상세 조회
   */
  async getQuoteInquiry(
    companyId: number,
    inquiryId: number
  ): Promise<QuoteInquiryDetail> {
    const url = `/api/company/${companyId}/quote-inquiries/${inquiryId}`;

    const response = await httpClient.get<{
      data: QuoteInquiryDetail;
      errorMsg: string | null;
      errorCode: string | null;
    }>(url);

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "견적 문의 상세 정보를 가져오지 못했습니다."
      );
    }

    return response.data.data;
  }

  /**
   * 견적 문의 답변 발송
   */
  async replyQuoteInquiry(
    companyId: number,
    inquiryId: number,
    request: ReplyQuoteInquiryRequest
  ): Promise<QuoteInquiryDetail> {
    const url = `/api/company/${companyId}/quote-inquiries/${inquiryId}/reply`;

    const response = await httpClient.post<{
      data: QuoteInquiryDetail;
      errorMsg: string | null;
      errorCode: string | null;
    }>(url, request);

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "답변 발송에 실패했습니다.");
    }

    return response.data.data;
  }

  /**
   * 견적 문의 상태 변경
   */
  async updateQuoteInquiryStatus(
    companyId: number,
    inquiryId: number,
    request: UpdateQuoteInquiryStatusRequest
  ): Promise<QuoteInquiryDetail> {
    const url = `/api/company/${companyId}/quote-inquiries/${inquiryId}/status`;

    const response = await httpClient.patch<{
      data: QuoteInquiryDetail;
      errorMsg: string | null;
      errorCode: string | null;
    }>(url, request);

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상태 변경에 실패했습니다.");
    }

    return response.data.data;
  }
}

export const companyQuoteInquiryApiService = new CompanyQuoteInquiryApiService();
